import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatUTCTimeOnlyToLocal } from '../../../utils/timezone';
import {
  getAdminHeaders,
  hasPermission,
  PERMS,
  validateAdminSession,
} from '../../../utils/adminAuth';
import './ReportsCalendarPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface DaySummary {
  date: string;
  uniqueVolunteers: number;
  onsiteCount: number;
  remoteCount: number;
  sessionCount: number;
}

interface CalendarSession {
  id: number;
  volunteerId: number;
  firstName: string;
  lastName: string;
  taskName: string;
  taskCategory: string;
  taskColor?: string;
  checkInTime: string;
  checkOutTime?: string | null;
  workLocation: string;
  wasAutoCheckedOut?: boolean;
  isOpen?: boolean;
  durationHours?: number;
}

type DayMode = 'time' | 'role' | 'timeline';
type LocationFilter = 'all' | 'On-site' | 'Remote';

const monthLabel = (year: number, month: number) =>
  new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const formatDayHeading = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDuration = (hours?: number, isOpen?: boolean) => {
  if (isOpen) return 'In progress';
  if (hours == null || Number.isNaN(hours)) return '—';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

const ReportsCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dayMap, setDayMap] = useState<Record<string, DaySummary>>({});
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [monthError, setMonthError] = useState('');

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [daySessions, setDaySessions] = useState<CalendarSession[]>([]);
  const [dayMeta, setDayMeta] = useState({ uniqueVolunteers: 0, sessionCount: 0 });
  const [loadingDay, setLoadingDay] = useState(false);
  const [dayError, setDayError] = useState('');
  const [dayMode, setDayMode] = useState<DayMode>('timeline');
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('all');

  useEffect(() => {
    const boot = async () => {
      const session = await validateAdminSession();
      if (!session) {
        navigate('/admin-login');
        return;
      }
      if (session.mustResetPassword) {
        navigate('/admin/change-password');
        return;
      }
      if (!hasPermission(PERMS.reportsCalendarView, session) && !hasPermission(PERMS.reportsCalendarRead, session)) {
        navigate('/admin-panel');
      }
    };
    boot();
  }, [navigate]);

  const fetchMonth = useCallback(async () => {
    setLoadingMonth(true);
    setMonthError('');
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      const response = await fetch(`${API_BASE_URL}/admin/calendar?${params}`, {
        headers: getAdminHeaders(),
      });
      if (response.status === 401) {
        navigate('/admin-login');
        return;
      }
      const data = await response.json();
      if (!response.ok || !data.success) {
        setMonthError(data.message || 'Failed to load calendar');
        setDayMap({});
        return;
      }
      const map: Record<string, DaySummary> = {};
      (data.days || []).forEach((d: DaySummary) => {
        map[d.date] = d;
      });
      setDayMap(map);
    } catch {
      setMonthError('Failed to connect to server');
      setDayMap({});
    } finally {
      setLoadingMonth(false);
    }
  }, [year, month, navigate]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  const goPrevMonth = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  };

  const openDay = async (dateStr: string) => {
    setSelectedDate(dateStr);
    setPanelOpen(true);
    setLoadingDay(true);
    setDayError('');
    setDaySessions([]);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/calendar/day?date=${encodeURIComponent(dateStr)}`,
        { headers: getAdminHeaders() }
      );
      if (response.status === 401) {
        navigate('/admin-login');
        return;
      }
      const data = await response.json();
      if (!response.ok || !data.success) {
        setDayError(data.message || 'Failed to load day');
        return;
      }
      setDaySessions(data.sessions || []);
      setDayMeta({
        uniqueVolunteers: data.uniqueVolunteers || 0,
        sessionCount: data.sessionCount || 0,
      });
    } catch {
      setDayError('Failed to connect to server');
    } finally {
      setLoadingDay(false);
    }
  };

  const closeDay = () => {
    setPanelOpen(false);
  };

  const calendarCells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells: Array<{ dateStr: string | null; dayNum: number | null }> = [];

    for (let i = 0; i < startPad; i++) {
      cells.push({ dateStr: null, dayNum: null });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ dateStr, dayNum: d });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ dateStr: null, dayNum: null });
    }
    return cells;
  }, [year, month]);

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const filteredSessions = useMemo(() => {
    if (locationFilter === 'all') return daySessions;
    return daySessions.filter((s) => s.workLocation === locationFilter);
  }, [daySessions, locationFilter]);

  const roleGroups = useMemo(() => {
    const groups = new Map<string, { color: string; sessions: CalendarSession[] }>();
    filteredSessions.forEach((s) => {
      const key = s.taskCategory || 'Other';
      if (!groups.has(key)) {
        groups.set(key, { color: s.taskColor || '#e5e7eb', sessions: [] });
      }
      groups.get(key)!.sessions.push(s);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredSessions]);

  const timelineRows = useMemo(() => {
    const byPerson = new Map<number, { volunteerId: number; name: string; firstStart: number; sessions: CalendarSession[] }>();
    filteredSessions.forEach((s) => {
      const start = new Date(s.checkInTime).getTime();
      const existing = byPerson.get(s.volunteerId);
      if (!existing) {
        byPerson.set(s.volunteerId, {
          volunteerId: s.volunteerId,
          name: `${s.firstName} ${s.lastName}`.trim(),
          firstStart: start,
          sessions: [s],
        });
        return;
      }
      existing.sessions.push(s);
      if (start < existing.firstStart) existing.firstStart = start;
    });
    return Array.from(byPerson.values()).sort((a, b) => a.firstStart - b.firstStart);
  }, [filteredSessions]);

  const timelineRange = useMemo(() => {
    if (filteredSessions.length === 0) {
      return { startMs: 0, endMs: 1, hours: [] as Date[] };
    }
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    const nowMs = Date.now();
    filteredSessions.forEach((s) => {
      const start = new Date(s.checkInTime).getTime();
      const end = s.isOpen || !s.checkOutTime ? nowMs : new Date(s.checkOutTime).getTime();
      if (start < min) min = start;
      if (end > max) max = end;
    });
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      max = min + 60 * 60 * 1000;
    }
    const pad = 30 * 60 * 1000;
    const startMs = min - pad;
    const endMs = max + pad;
    const hours: Date[] = [];
    const firstHour = new Date(startMs);
    firstHour.setMinutes(0, 0, 0);
    if (firstHour.getTime() < startMs) {
      firstHour.setHours(firstHour.getHours() + 1);
    }
    for (let t = firstHour.getTime(); t <= endMs; t += 60 * 60 * 1000) {
      hours.push(new Date(t));
    }
    return { startMs, endMs, hours };
  }, [filteredSessions]);

  const barStyle = (session: CalendarSession) => {
    const span = timelineRange.endMs - timelineRange.startMs;
    const start = new Date(session.checkInTime).getTime();
    const end =
      session.isOpen || !session.checkOutTime
        ? Date.now()
        : new Date(session.checkOutTime).getTime();
    const left = Math.max(0, ((start - timelineRange.startMs) / span) * 100);
    const right = Math.min(100, ((end - timelineRange.startMs) / span) * 100);
    const width = Math.max(right - left, 0.8);
    return {
      left: `${left}%`,
      width: `${width}%`,
      backgroundColor: session.taskColor || '#86efac',
    };
  };

  return (
    <div className="calendar-container">
      <div className={`calendar-card ${panelOpen ? 'day-open' : ''}`}>
        <div className="calendar-month">
        <div className="calendar-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="calendar-title">Reports, Calendar</h1>
          <p className="calendar-subtitle">
            See who worked each day, by time, role, or timeline
          </p>
        </div>

        <div className="calendar-legend">
          <span className="calendar-legend-item">
            <span className="cell-count onsite" aria-hidden="true" />
            On-site
          </span>
          <span className="calendar-legend-item">
            <span className="cell-count remote" aria-hidden="true" />
            Remote
          </span>
        </div>

        <div className="calendar-nav">
          <button type="button" onClick={goPrevMonth} aria-label="Previous month">
            ←
          </button>
          <span className="calendar-month-label">{monthLabel(year, month)}</span>
          <button type="button" onClick={goNextMonth} aria-label="Next month">
            →
          </button>
          <button type="button" className="calendar-today-btn" onClick={goToday}>
            Today
          </button>
        </div>

        {monthError && <div className="calendar-error">{monthError}</div>}
        {loadingMonth && <div className="calendar-loading">Loading calendar…</div>}

        <div className="calendar-shell">
          <div className="calendar-weekday-row">
            {WEEKDAYS.map((d) => (
              <div key={d} className="calendar-weekday">
                {d}
              </div>
            ))}
          </div>
          <div className="calendar-grid">
            {calendarCells.map((cell, idx) => {
              if (!cell.dateStr) {
                return <div key={`empty-${idx}`} className="calendar-cell empty" />;
              }
              const summary = dayMap[cell.dateStr];
              const isToday = cell.dateStr === todayStr;
              const onsiteCount = summary?.onsiteCount || 0;
              const remoteCount = summary?.remoteCount || 0;
              const hasPeople = onsiteCount > 0 || remoteCount > 0;
              return (
                <button
                  key={cell.dateStr}
                  type="button"
                  className={`calendar-cell ${isToday ? 'today' : ''} ${hasPeople ? 'has-data' : ''}`}
                  onClick={() => openDay(cell.dateStr!)}
                >
                  <span className="cell-day-num">{cell.dayNum}</span>
                  {hasPeople && (
                    <span className="cell-counts">
                      {onsiteCount > 0 && (
                        <span className="cell-count onsite" title={`${onsiteCount} on-site`}>
                          {onsiteCount}
                        </span>
                      )}
                      {remoteCount > 0 && (
                        <span className="cell-count remote" title={`${remoteCount} remote`}>
                          {remoteCount}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="calendar-footer">
          <button type="button" className="back-button" onClick={() => navigate('/admin-panel')}>
            ← Back to Admin Panel
          </button>
        </div>
        </div>

        <div className={`calendar-day-panel ${panelOpen ? 'open' : ''}`} aria-hidden={!panelOpen}>
        <div className="day-panel-header">
          <div>
            <h2>{selectedDate ? formatDayHeading(selectedDate) : 'Day'}</h2>
            {!loadingDay && !dayError && (
              <p className="day-panel-meta">
                {dayMeta.uniqueVolunteers} volunteer{dayMeta.uniqueVolunteers === 1 ? '' : 's'} ·{' '}
                {dayMeta.sessionCount} session{dayMeta.sessionCount === 1 ? '' : 's'}
              </p>
            )}
          </div>
          <button type="button" className="day-panel-close" onClick={closeDay} aria-label="Close">
            ×
          </button>
        </div>

        <div className="day-panel-controls">
          <div className="day-mode-toggle">
            <button
              type="button"
              className={dayMode === 'timeline' ? 'active' : ''}
              onClick={() => setDayMode('timeline')}
            >
              Timeline
            </button>
            <button
              type="button"
              className={dayMode === 'role' ? 'active' : ''}
              onClick={() => setDayMode('role')}
            >
              By role
            </button>
            <button
              type="button"
              className={dayMode === 'time' ? 'active' : ''}
              onClick={() => setDayMode('time')}
            >
              By time
            </button>
          </div>
          <div className="day-location-filter">
            {(['all', 'On-site', 'Remote'] as LocationFilter[]).map((loc) => (
              <button
                key={loc}
                type="button"
                className={locationFilter === loc ? 'active' : ''}
                onClick={() => setLocationFilter(loc)}
              >
                {loc === 'all' ? 'All' : loc}
              </button>
            ))}
          </div>
        </div>

        {dayMode !== 'timeline' && (
          <p className="day-auto-note">Any duration with an underline was an auto checkout.</p>
        )}
        {dayMode === 'timeline' && (
          <p className="day-auto-note">
            <span className="timeline-swipe-hint">Swipe sideways to move through the day. </span>
            Each bar is a session. Overlapping bars mean people were working at the same time.
          </p>
        )}

        <div className="day-panel-body">
          {loadingDay && <div className="calendar-loading">Loading day…</div>}
          {dayError && <div className="calendar-error">{dayError}</div>}
          {!loadingDay && !dayError && filteredSessions.length === 0 && (
            <div className="day-empty">No sessions for this day.</div>
          )}

          {!loadingDay && !dayError && dayMode === 'time' && (
            <ul className="day-session-list">
              {filteredSessions.map((s) => (
                <li key={s.id} className="day-session-row">
                  <div className="session-name">
                    {s.firstName} {s.lastName}
                  </div>
                  <div className="session-detail">
                    <span
                      className={s.wasAutoCheckedOut ? 'duration-auto-checkout' : undefined}
                    >
                      {formatDuration(s.durationHours, s.isOpen)}
                    </span>
                    {' · '}
                    {formatUTCTimeOnlyToLocal(s.checkInTime)}
                    {' – '}
                    {s.isOpen || !s.checkOutTime
                      ? 'now'
                      : formatUTCTimeOnlyToLocal(s.checkOutTime)}
                    {' · '}
                    {s.taskName}
                    {' · '}
                    {s.workLocation}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {!loadingDay && !dayError && dayMode === 'timeline' && (
            <div
              className="timeline-wrap"
              style={{ '--timeline-hours': Math.max(timelineRange.hours.length, 1) } as React.CSSProperties}
            >
              <div className="timeline-header">
                <div className="timeline-name-col" />
                <div className="timeline-track-col">
                  {timelineRange.hours.map((hour) => {
                    const left =
                      ((hour.getTime() - timelineRange.startMs) /
                        (timelineRange.endMs - timelineRange.startMs)) *
                      100;
                    return (
                      <span
                        key={hour.toISOString()}
                        className="timeline-hour"
                        style={{ left: `${left}%` }}
                      >
                        {hour.toLocaleTimeString('en-US', { hour: 'numeric' })}
                      </span>
                    );
                  })}
                </div>
              </div>
              {timelineRows.map((row) => (
                <div key={row.volunteerId} className="timeline-row">
                  <div className="timeline-name-col" title={row.name}>
                    {row.name}
                  </div>
                  <div className="timeline-track-col">
                    {timelineRange.hours.map((hour) => {
                      const left =
                        ((hour.getTime() - timelineRange.startMs) /
                          (timelineRange.endMs - timelineRange.startMs)) *
                        100;
                      return (
                        <span
                          key={`${row.volunteerId}-${hour.toISOString()}`}
                          className="timeline-gridline"
                          style={{ left: `${left}%` }}
                        />
                      );
                    })}
                    {row.sessions.map((s) => (
                      <div
                        key={s.id}
                        className={`timeline-bar ${s.wasAutoCheckedOut ? 'auto-checkout' : ''} ${s.isOpen ? 'open' : ''}`}
                        style={barStyle(s)}
                        title={`${s.firstName} ${s.lastName} · ${formatUTCTimeOnlyToLocal(s.checkInTime)} – ${
                          s.isOpen || !s.checkOutTime ? 'now' : formatUTCTimeOnlyToLocal(s.checkOutTime)
                        } · ${s.taskName} · ${s.workLocation}`}
                      >
                        <span className="timeline-bar-label">{s.taskName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loadingDay && !dayError && dayMode === 'role' && (
            <div className="day-role-groups">
              {roleGroups.map(([category, group]) => (
                <section key={category} className="role-group">
                  <h3 className="role-group-title">
                    <span
                      className="role-color-dot"
                      style={{ backgroundColor: group.color }}
                    />
                    {category}
                    <span className="role-count">{group.sessions.length}</span>
                  </h3>
                  <ul className="day-session-list">
                    {group.sessions.map((s) => (
                      <li key={s.id} className="day-session-row">
                        <div className="session-name">
                          {s.firstName} {s.lastName}
                        </div>
                        <div className="session-detail">
                          <span
                            className={s.wasAutoCheckedOut ? 'duration-auto-checkout' : undefined}
                          >
                            {formatDuration(s.durationHours, s.isOpen)}
                          </span>
                          {' · '}
                          {formatUTCTimeOnlyToLocal(s.checkInTime)}
                          {' – '}
                          {s.isOpen || !s.checkOutTime
                            ? 'now'
                            : formatUTCTimeOnlyToLocal(s.checkOutTime)}
                          {' · '}
                          {s.taskName}
                          {' · '}
                          {s.workLocation}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ReportsCalendarPage;
